import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import ProvisioningStyles from 'pages/provisioning/application-gateway/style';
import {
    environmentDetails,
    isMandatoryTagsFullfilled,
    orderId,
    sspSubmitOrder,
    toggleSspModels,
    isAppUrlValid,
    isValidBackendHealthProbe,
    isValidFqdns,
    isValidISANumber,
    isValidScanNumber,
    // uploadedFilesState,
    isValidOwners,
    // isValidgtmLoadBalancer,
} from 'pages/provisioning/application-gateway/store';
import { ApplicationGateWayURLCatalogDefinitionID, ExternalPublicLoadBalancerURLCatalogDefinitionID } from 'utils/catalogDefinitionId';
import { resetEnvironmentDetails, resetOrder, resetToggleSspModal, formOrderObjFromOrderDetails } from './utils';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/application-gateway/ProvisioningHeader'));
const SspForm = React.lazy(() => import('pages/provisioning/application-gateway/SspForm'));
const OrderSuccessDialog = React.lazy(() => import('pages/provisioning/application-gateway/OrderSuccessDialog'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/application-gateway/ShowCostDetails'));

const ApplicationGateWayProvisioning = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    // const [, setUploadedFiles] = useRecoilState(uploadedFilesState);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetEnvDetails] = useRecoilState(environmentDetails);
    const [, resetIsMandatoryTagsFullfilled] = useRecoilState(isMandatoryTagsFullfilled);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [, resetValidOwners] = useRecoilState(isValidOwners);
    const resetAppUrlValid = useResetRecoilState(isAppUrlValid);
    const resetisValidBackendHealthProbe = useResetRecoilState(isValidBackendHealthProbe);
    const resetisValidFqdns = useResetRecoilState(isValidFqdns);
    const resetValidISANumber = useResetRecoilState(isValidISANumber);
    const resetValidScanNumber = useResetRecoilState(isValidScanNumber);
    // const [, resetGTMLoadbalencerValid] = useRecoilState(isValidgtmLoadBalancer);
    // const resetUploadFiles = useResetRecoilState(uploadedFilesState);

    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const axios = useAxios();

    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);

        return data;
    }
    async function checkCatalog() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}`);
        const costs = await getCosts();
        if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetails}/${cartItemId}`);
            const orderObj = formOrderObjFromOrderDetails(catalogServiceId, orderDetails);
            // const uploadObj = formUploadObjFromOrderDetails(catalogServiceId, orderDetails);
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                emptyCost: costs,
            }));
            // setUploadedFiles(uploadObj);
        } else {
            setOrder((previous) => ({
                ...previous,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }

    const reset = () => {
        setOrder({ ...order, ...resetOrder });
        resetOrderId('');
        resetAppUrlValid();
        resetEnvDetails({
            ...resetEnvironmentDetails,
            ...(ApplicationGateWayURLCatalogDefinitionID.includes(catalogCode) && {
                'Application gateway Name': '---',
            }),
            ...(ExternalPublicLoadBalancerURLCatalogDefinitionID.includes(catalogCode) && {
                'Public Load Balancer Name': '---',
            }),
        });
        resetIsMandatoryTagsFullfilled(false);
        resetToggleSspModels(resetToggleSspModal);
        resetisValidBackendHealthProbe();
        resetisValidFqdns();
        resetValidISANumber();
        resetValidScanNumber();
        // resetUploadFiles();
        resetValidOwners({
            isvalidAppOwner: true,
            isvalidCertificateOwner1: true,
            isvalidCertificateOwner2: true,
        });
        // resetGTMLoadbalencerValid(true);
    };

    React.useEffect(() => {
        if (catalogCode) {
            setLoading(true);
            reset();
            checkCatalog();
        }
    }, [catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);
    return (
        <>
            <Helmet>
                <title>
                    {ApplicationGateWayURLCatalogDefinitionID.includes(catalogCode)
                        ? 'Application Gateway URL Onboarding'
                        : 'Public Load Balancer URL Onboarding'}
                </title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} data-testid="index-form" className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <OrderSuccessDialog />
                        <ShowCostDetails />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default ApplicationGateWayProvisioning;
